import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  ArrowRight,
  BarChart3,
  CalendarCheck,
  Eye,
  EyeOff,
  Gift,
  Loader2,
  Lock,
  Mail,
  ShieldCheck,
  Sparkles,
  Target,
} from 'lucide-react'
import { SocialSignInButtons } from '@adthlete/shared-frontend'
import { loginThunk } from '../redux/userSlice'
import { useAuth } from '@/hooks/useAuth'
import type { AppDispatch } from '../store'
import api from '../lib/api'
import licensingPhoto from '@/assets/website/licensing.jpg'
import appearancePhoto from '@/assets/website/appearance.jpg'
import giftingPhoto from '@/assets/website/gifting.jpg'

const LOGOMARK = 'https://adthlete.us-iad-1.linodeobjects.com/public/adthlete_logomark.png'

/**
 * Photos and wording from athios.ai (the home page and For Brands), so the brand app says what the
 * website says. The three photos are the three ways brands work with athletes.
 */
const COLLAGE = {
  licensing: {
    src: licensingPhoto,
    alt: 'A creator applying a skincare serum in front of a ring light and phone',
  },
  appearance: {
    src: appearancePhoto,
    alt: 'An athlete signing posters for fans at an appearance',
  },
  gifting: { src: giftingPhoto, alt: 'An athlete unboxing a gifted pair of sneakers' },
}

const FEATURES = [
  {
    icon: Target,
    title: 'Matching, not guessing',
    body: 'Athletes matched by audience overlap, product interest, and the content they already create.',
  },
  {
    icon: ShieldCheck,
    title: 'Every workflow handled',
    body: 'Matching, contracts, compliance, and payment. You run the partnership, not the paperwork.',
  },
  {
    icon: BarChart3,
    title: 'Know what worked',
    body: 'Real time reporting on organic and paid. Measure ROAS like an ad campaign.',
  },
]

/** What went wrong signing in with Google, by the code the backend sends back */
const OAUTH_ERRORS: Record<string, string> = {
  no_account: "There's no Athios brand account with that email yet. Create one below.",
  wrong_app:
    'That email is registered in another Athios app (athlete, manager or admin). Contact us if you need accounts in more than one app.',
  ambiguous_account:
    "More than one brand account matches that email, so we can't tell which to use. Sign in another way.",
  email_unverified:
    "We couldn't confirm your email with that provider. Try another way to sign in.",
  account_closed: 'This account has been closed.',
  expired: 'That took a little too long. Please try again.',
  cancelled: 'Sign-in was cancelled.',
  not_configured: "That sign-in option isn't available yet.",
  invalid_token: "We couldn't verify that sign-in. Please try again.",
  bad_request: 'Something went wrong. Please try again.',
}

const fieldCls =
  'h-12 w-full rounded-xl border border-[#E5E7EB] bg-[#FFFFFF] pl-11 pr-4 text-base text-[#111827] placeholder:text-[#9CA3AF] outline-none transition focus:border-[#84CC16] focus:ring-4 focus:ring-[#C0FD72]/40'

const Logo: React.FC<{ size?: 'md' | 'lg' }> = ({ size = 'md' }) => (
  <span className="inline-flex items-center gap-2.5">
    <img src={LOGOMARK} alt="" className={size === 'lg' ? 'h-11 w-11' : 'h-9 w-9'} />
    <span
      className={`font-black tracking-tight text-[#0F172A] ${size === 'lg' ? 'text-4xl' : 'text-3xl'}`}
    >
      Athios
    </span>
  </span>
)

const Chip: React.FC<{
  icon: React.ComponentType<{ className?: string }>
  children: React.ReactNode
  className: string
}> = ({ icon: Icon, children, className }) => (
  <div
    className={`absolute flex items-center gap-3 rounded-2xl bg-[#FFFFFF] px-4 py-3 text-sm leading-tight text-[#111827] shadow-[0_12px_30px_rgba(16,24,40,0.12)] ${className}`}
  >
    <Icon className="h-7 w-7 shrink-0 text-[#4D7C0F]" />
    <span>{children}</span>
  </div>
)

/**
 * The brand app's sign-in: what Athios is on the left, and signing in on the right with a
 * password, Google or an emailed link. "Create an account" switches the card to sign-up.
 */
const SignIn: React.FC = () => {
  const [searchParams] = useSearchParams()
  const inviteEmail = searchParams.get('email')
  const returnTo = searchParams.get('returnTo')
  const oauthError = searchParams.get('oauth_error')
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const { marketer, requestMagicLink } = useAuth()

  const [mode, setMode] = useState<'signin' | 'signup'>(
    oauthError === 'no_account' ? 'signup' : 'signin',
  )
  const [email, setEmail] = useState(
    inviteEmail || (import.meta.env.DEV ? 'marketer@test.com' : ''),
  )
  const [password, setPassword] = useState(import.meta.env.DEV ? '321pass' : '')
  const [showPassword, setShowPassword] = useState(false)
  const [signUpEmail, setSignUpEmail] = useState(
    oauthError === 'no_account' ? (inviteEmail ?? '') : '',
  )
  const [error, setError] = useState<React.ReactNode>(oauthError ? OAUTH_ERRORS[oauthError] : '')
  const [notice, setNotice] = useState('')
  const [busy, setBusy] = useState<'' | 'password' | 'link' | 'signup'>('')

  useEffect(() => {
    if (marketer?.id) {
      navigate(returnTo ? decodeURIComponent(returnTo) : '/', { replace: true })
    }
  }, [marketer, navigate, returnTo])

  const switchMode = (m: 'signin' | 'signup') => {
    setMode(m)
    setError('')
    setNotice('')
  }

  const signIn = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setBusy('password')
    try {
      await dispatch(loginThunk({ email, password })).unwrap()
    } catch (err: any) {
      if (err?.status === 403) navigate(`/confirmation-sent?email=${encodeURIComponent(email)}`)
      else setError(err?.message || 'Sign-in failed. Please try again.')
    } finally {
      setBusy('')
    }
  }

  const emailLink = async () => {
    setError('')
    setNotice('')
    if (!email) {
      setError('Enter your email address first.')
      return
    }
    setBusy('link')
    try {
      await requestMagicLink(email)
      setNotice(`Check ${email} for a link to sign in.`)
    } catch (err: any) {
      setError(err?.message || "Couldn't send the link. Please try again.")
    } finally {
      setBusy('')
    }
  }

  const signUp = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setBusy('signup')
    try {
      const { data } = await api.post('/users/check-email', { email: signUpEmail })
      if (data.exists) {
        setError(
          <>
            There's already an account with that email.{' '}
            <button
              type="button"
              onClick={() => {
                setEmail(signUpEmail)
                switchMode('signin')
              }}
              className="font-semibold text-[#3F7A0A] underline"
            >
              Sign in instead
            </button>
          </>,
        )
        return
      }
      navigate(`/signup?email=${encodeURIComponent(signUpEmail)}`)
    } catch {
      setError("Couldn't check that email. Please try again.")
    } finally {
      setBusy('')
    }
  }

  return (
    <div className="min-h-screen bg-[#F4F7EF] p-3 sm:p-5">
      <div className="mx-auto grid min-h-[calc(100vh-1.5rem)] max-w-[1440px] gap-6 rounded-[28px] bg-[linear-gradient(135deg,#FAFCF7_0%,#F1F7E9_100%)] p-4 sm:min-h-[calc(100vh-2.5rem)] sm:p-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,520px)] lg:p-12">
        {/* What Athios is */}
        <section className="hidden min-w-0 lg:grid lg:grid-cols-1 lg:items-center xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] xl:gap-8">
          <div className="max-w-[460px]">
            <Logo size="lg" />
            <p className="mt-12 text-sm font-medium uppercase tracking-wider text-[#4B5563]">
              The Athlete Content Marketplace
            </p>
            <h1 className="mt-3 text-5xl font-black leading-[1.05] tracking-tight text-[#0F172A] [text-wrap:balance]">
              Athlete content that works for any business.
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-[#4B5563]">
              From the shop down the street to national brands. Gift products, book appearances, or
              license content, then scale what works.
            </p>
            <ul className="mt-10 space-y-7">
              {FEATURES.map((f) => (
                <li key={f.title} className="flex gap-5">
                  <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-[#ECFCCB] text-[#4D7C0F]">
                    <f.icon className="h-7 w-7" />
                  </span>
                  <span>
                    <span className="block text-lg font-bold text-[#0F172A]">{f.title}</span>
                    <span className="block text-base text-[#4B5563]">{f.body}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div
            className="relative mx-auto hidden h-[640px] w-full max-w-[440px] xl:block"
            aria-hidden
          >
            <div className="absolute left-[8%] top-[10%] h-[80%] w-[80%] rotate-6 rounded-[48px] bg-[#E2F5C9]" />
            <img
              src={COLLAGE.licensing.src}
              alt={COLLAGE.licensing.alt}
              className="absolute left-0 top-[4%] h-[56%] w-[62%] -rotate-[4deg] rounded-[28px] object-cover object-top shadow-xl"
            />
            <img
              src={COLLAGE.appearance.src}
              alt={COLLAGE.appearance.alt}
              className="absolute right-0 top-[28%] h-[48%] w-[56%] rotate-[4deg] rounded-[28px] object-cover object-top shadow-xl"
            />
            <img
              src={COLLAGE.gifting.src}
              alt={COLLAGE.gifting.alt}
              className="absolute bottom-[2%] left-[2%] h-[46%] w-[58%] -rotate-[3deg] rounded-[28px] object-cover shadow-xl"
            />
            <Chip icon={Sparkles} className="right-[-4%] top-[20%]">
              <span className="font-semibold">Content licensing</span>
              <br />
              <span className="text-[#4B5563]">License what already works</span>
            </Chip>
            <Chip icon={CalendarCheck} className="left-[2%] top-[46%]">
              <span className="font-semibold">Appearances</span>
              <br />
              <span className="text-[#4B5563]">Book athletes in person</span>
            </Chip>
            <Chip icon={Gift} className="bottom-[10%] right-[4%]">
              <span className="font-semibold">Gifting</span>
              <br />
              <span className="text-[#4B5563]">Send product. Get real posts.</span>
            </Chip>
          </div>
        </section>

        {/* Sign in / create an account */}
        <section className="flex min-w-0 items-center justify-center">
          <div className="w-full max-w-[520px] rounded-[28px] bg-[#FFFFFF] px-5 py-8 shadow-[0_20px_60px_rgba(16,24,40,0.08)] sm:px-10 sm:py-12">
            <div className="flex justify-center">
              <Logo />
            </div>
            <h2 className="mt-6 text-center text-3xl font-black tracking-tight text-[#0F172A] sm:text-4xl">
              {mode === 'signin' ? 'Welcome back' : 'Create your account'}
            </h2>
            <p className="mt-2 text-center text-[#4B5563]">
              {mode === 'signin'
                ? 'Sign in to your brand account'
                : 'Start finding athletes for your brand'}
            </p>

            {error ? (
              <p
                role="alert"
                className="mt-6 rounded-xl border border-[#FECACA] bg-[#FEF2F2] px-4 py-3 text-sm text-[#B91C1C]"
              >
                {error}
              </p>
            ) : null}
            {notice ? (
              <p
                role="status"
                className="mt-6 rounded-xl border border-[#D9F99D] bg-[#F7FEE7] px-4 py-3 text-sm text-[#3F6212]"
              >
                {notice}
              </p>
            ) : null}

            {mode === 'signin' ? (
              <>
                {/* New brands first: the big button creates an account, signing in is the quiet one */}
                <div className="mt-7 rounded-2xl border border-[#D9F99D] bg-[#F7FEE7] p-4 text-center">
                  <p className="text-sm font-semibold text-[#1A2E05]">New to Athios?</p>
                  <p className="mt-0.5 text-sm text-[#4B5563]">
                    Set up your brand in two minutes and start browsing athletes.
                  </p>
                  <button
                    type="button"
                    onClick={() => switchMode('signup')}
                    className="mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#C0FD72] text-base font-semibold text-[#0F172A] transition-colors hover:bg-[#B2F25E]"
                  >
                    Create an account <ArrowRight className="h-5 w-5" />
                  </button>
                </div>

                <div className="my-6 flex items-center gap-3 text-xs font-medium uppercase tracking-wider text-[#6B7280]">
                  <span className="h-px flex-1 bg-[#E5E7EB]" /> Already have an account?
                  <span className="h-px flex-1 bg-[#E5E7EB]" />
                </div>

                <form onSubmit={signIn} data-track-form="Sign in" className="space-y-5">
                  <div>
                    <label
                      htmlFor="email"
                      className="mb-2 block text-sm font-medium text-[#374151]"
                    >
                      Email address
                    </label>
                    <div className="relative">
                      <Mail className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#6B7280]" />
                      <input
                        id="email"
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@brand.com"
                        className={fieldCls}
                      />
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="password"
                      className="mb-2 block text-sm font-medium text-[#374151]"
                    >
                      Password
                    </label>
                    <div className="relative">
                      <Lock className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#6B7280]" />
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        className={`${fieldCls} pr-12`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((s) => !s)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-[#6B7280] hover:text-[#111827]"
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                    <div className="mt-2 text-right">
                      <button
                        type="button"
                        onClick={() =>
                          navigate(
                            `/forgot-password${email ? `?email=${encodeURIComponent(email)}` : ''}`,
                          )
                        }
                        className="text-sm font-medium text-[#3F7A0A] underline underline-offset-2"
                      >
                        Forgot password?
                      </button>
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={!!busy}
                    className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#0F172A] text-base font-semibold text-[#FFFFFF] transition-colors hover:bg-[#1E293B] disabled:opacity-60"
                  >
                    {busy === 'password' ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                    Sign in <ArrowRight className="h-5 w-5" />
                  </button>
                </form>

                <div className="my-6 flex items-center gap-3 text-xs font-medium uppercase tracking-wider text-[#6B7280]">
                  <span className="h-px flex-1 bg-[#E5E7EB]" /> Or continue with
                  <span className="h-px flex-1 bg-[#E5E7EB]" />
                </div>

                <SocialSignInButtons
                  userType="marketer"
                  returnTo={returnTo}
                  onUnavailable={(p) =>
                    setNotice(
                      `Sign in with ${p} is coming soon. Use your password or an emailed link for now.`,
                    )
                  }
                />
                <button
                  type="button"
                  onClick={() => emailLink()}
                  disabled={!!busy}
                  className="mt-3 flex h-12 w-full items-center justify-center gap-3 rounded-xl border border-[#E5E7EB] bg-[#FFFFFF] text-base font-medium text-[#111827] transition-colors hover:bg-[#F9FAFB] disabled:opacity-60"
                >
                  {busy === 'link' ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Mail className="h-5 w-5" />
                  )}
                  Email me a sign-in link
                </button>

                <p className="mt-8 text-center text-base text-[#4B5563]">
                  Don't have an account?{' '}
                  <button
                    type="button"
                    onClick={() => switchMode('signup')}
                    className="inline-flex items-center gap-1 font-semibold text-[#3F7A0A] underline underline-offset-2"
                  >
                    Create an account <ArrowRight className="h-4 w-4" />
                  </button>
                </p>
              </>
            ) : (
              <>
                <form onSubmit={signUp} data-track-form="Brand sign-up" className="mt-7 space-y-5">
                  <div>
                    <label
                      htmlFor="signup-email"
                      className="mb-2 block text-sm font-medium text-[#374151]"
                    >
                      Work email
                    </label>
                    <div className="relative">
                      <Mail className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#6B7280]" />
                      <input
                        id="signup-email"
                        type="email"
                        autoComplete="email"
                        required
                        value={signUpEmail}
                        onChange={(e) => setSignUpEmail(e.target.value)}
                        placeholder="you@brand.com"
                        className={fieldCls}
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={!!busy}
                    className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#C0FD72] text-base font-semibold text-[#0F172A] transition-colors hover:bg-[#B2F25E] disabled:opacity-60"
                  >
                    {busy === 'signup' ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                    Continue <ArrowRight className="h-5 w-5" />
                  </button>
                </form>
                <p className="mt-4 text-center text-sm text-[#6B7280]">
                  Next you'll add your name, a password and your brand.
                </p>
                <p className="mt-8 text-center text-base text-[#4B5563]">
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => switchMode('signin')}
                    className="font-semibold text-[#3F7A0A] underline underline-offset-2"
                  >
                    Sign in
                  </button>
                </p>
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  )
}

export default SignIn
